import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Box, CircularProgress, IconButton, Stack, Typography } from "@mui/material"
import { ExpandMore } from "@mui/icons-material"
import { DateTime } from "luxon"

import CustomLink from "widgets/common/CustomLink"
import EntityIcon from "widgets/common/EntityIcon"
import RelativeTime from "widgets/dates/RelativeTime"
import useRoutes from "helpers/hooks/useRoutes"
import type { AlertSettingAccordion } from "types/alerts.types"
import AlertAccordionMenu from "widgets/alerts/AlertAccordionMenu"
import usePermissions from "helpers/hooks/usePermissions"
import { getAlertFields, getAlertLinkHref, getAlertStateIcon } from "helpers/utils/alerts"
import { getTelemetryTranslation, nameOrUnnamed } from "helpers/utils/translations"

interface AlertAccordionSummaryContentProps {
  alert: AlertSettingAccordion
  hideEntity?: boolean
  expanded: boolean
  onToggle: () => void
  setActive: (active: boolean) => void
  isSettingActive: boolean
}

const AlertAccordionSummaryContent = ({
  alert,
  hideEntity,
  expanded,
  onToggle,
  setActive,
  isSettingActive,
}: AlertAccordionSummaryContentProps) => {
  const { t } = useTranslation()
  const { baseOrgURL } = useRoutes()
  const { isEditor } = usePermissions()

  const alertStateIcon = useMemo(() => getAlertStateIcon(alert.state), [alert.state])
  const alertLinkHref = useMemo(
    () => getAlertLinkHref(alert, baseOrgURL),
    [alert, baseOrgURL],
  )
  // Only custom alerts pick their field, the other types already name it.
  const typeLine = useMemo(() => {
    const type = t(`alert_types.${alert.type.toUpperCase()}`)
    const fields = alert.type === "threshold" ? getAlertFields(alert) : []
    return fields.length
      ? `${type} · ${fields.map((field) => getTelemetryTranslation(field, t)).join(", ")}`
      : type
  }, [alert, t])
  const columns = hideEntity
    ? "minmax(0, 9fr) minmax(0, 3fr)"
    : "minmax(0, 4fr) minmax(0, 5fr) minmax(0, 3fr)"
  // Below md the state icon shares the top row with the menu, and the rest goes under them.
  const fullRow = { gridColumn: { xs: "1 / -1", md: "auto" }, minWidth: 0 }
  const opacity = alert.state === "disabled" ? 0.5 : 1

  return (
    // The summary only opens from its chevron, so a click here selects text instead.
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr) auto",
          md: `auto ${columns} auto`,
        },
        alignItems: "center",
        columnGap: { xs: 1, md: 2 },
        rowGap: 2,
        width: "100%",
        ml: 0.5,
        overflowWrap: "anywhere",
        pointerEvents: "auto",
        userSelect: "text",
        cursor: "auto",
      }}
    >
      {/* The state is about to change, so its icon waits for the new one. */}
      <Stack direction={"row"} alignItems={"center"}>
        {isSettingActive ? (
          <CircularProgress size={20} sx={{ color: "neutral.600" }} />
        ) : (
          alertStateIcon
        )}
      </Stack>
      <Stack spacing={0.25} sx={{ ...fullRow, opacity }}>
        <Typography variant="h5">{alert.config.custom_name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {typeLine}
        </Typography>
      </Stack>
      {!hideEntity && (
        <Stack
          direction={"row"}
          alignItems={"center"}
          gap={1.5}
          sx={{ ...fullRow, opacity }}
        >
          <EntityIcon
            entity={alert.entity}
            variant="avatar"
            avatarSize={32}
            tooltip={t(`entities.${alert.entity.toUpperCase()}`)}
          />
          <Stack spacing={0.25} minWidth={0}>
            <CustomLink href={alertLinkHref} variant="h5" hover bold>
              {nameOrUnnamed(alert.name, t)}
            </CustomLink>
            <Typography variant="body2" color="text.secondary">
              {t("generic.ID")} {alert.entity_id}
            </Typography>
          </Stack>
        </Stack>
      )}
      <Stack spacing={0.25} sx={{ ...fullRow, opacity }}>
        <Typography variant="h5" component="div">
          {alert.triggered_at ? (
            <RelativeTime date={DateTime.fromISO(alert.triggered_at)} />
          ) : (
            "-"
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("alerts.LAST_TRIGGERED")}
        </Typography>
      </Stack>
      <Stack
        direction={"row"}
        alignItems={"center"}
        sx={{
          gridColumn: { xs: 2, md: "auto" },
          gridRow: { xs: 1, md: "auto" },
          // Keeps the top row as tall as the state icon.
          my: { xs: -0.75, md: 0 },
        }}
      >
        <Stack justifyContent={"center"} alignItems={"center"} minWidth={"34px"}>
          {isEditor && (
            <AlertAccordionMenu
              alert={alert}
              setActive={setActive}
              isSettingActive={isSettingActive}
            />
          )}
        </Stack>
        <IconButton
          size="small"
          onClick={onToggle}
          sx={(theme) => ({
            transform: expanded ? "rotate(180deg)" : "none",
            transition: theme.transitions.create("transform", {
              duration: theme.transitions.duration.shortest,
            }),
          })}
        >
          <ExpandMore sx={{ color: "text.primary" }} />
        </IconButton>
      </Stack>
    </Box>
  )
}

export default AlertAccordionSummaryContent
